<template>
  <div>
    <apex-charts
      ref="apex"
      type="donut"
      :width="size"
      :height="size"
      :options="options"
      :series="newSeries"
      @mouseleave.native="hideTooltip"
      @mousemove.native="handleMouseMove" />

    <c-tooltip
      :value="tooltipContent"
      position="top"
      dark
      ref="tooltip"
      :isHTML="false"
      :fontSize="14"
      :maxWidth="200"
      :arrowSpacing="15"
      :elementSpacing="0"
      :title="tooltipTitle"
      :show="tooltipVisible"
      :target="$refs.apex"
      :style="tooltipStyle"
      :customCoordinates="{ left: tooltipLeft, top: tooltipTop }"
      :titleMarker="tooltipMarker" />
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { applyBlurStrokeFilters } from '../helpers'

const DEFAULT_COLORS = [
  '#6FBAFF',
  '#BF6EFF',
  '#FF6FA3',
  '#FFAC6F',
  '#FFCE6F',
  '#5DE0A1'
]

const DEFAULT_MARKERS = [
  { type: 'bullet', value: 'disabled' },
  { type: 'bullet', value: 'support' },
  { type: 'bullet', value: 'primary' },
  { type: 'bullet', value: 'negative' },
  { type: 'bullet', value: 'complementary' },
  { type: 'bullet', value: 'alert' },
  { type: 'bullet', value: 'positive' },
]

export default {
  name: 'CGraphDonut',
  components: {
    ApexCharts: VueApexCharts
  },
  props: {
    /**
     * Sets the size of the donut chart.
     */
    size: { type: Number, default: 225 },

    /**
     * Array of values that determines the size of each donut slice.
     */
    series: { type: Array, default: () => [] },

    /**
     * Array of colors that determines the color of each donut slice. If nor
     * provided, the graph sets the default colors
     */
    customColors: { type: Array, default: () => [] },

    /**
     * Array of labels for each data point in the chart.
     */
    labels: { type: Array, default: () => [] },

    /**
     * Array of markers to display alongside the tooltip title.
     */
    customMarkers: { type: Array, default: () => [] },
    formatter: {
      type: Function,
      default: (val) => parseInt(val)
    },

    /**
     * Function to customize the tooltip content.
     * Receives an object with at least a `value` property.
     * @param {Object} params - Tooltip parameters.
     * @param {any} params.value - The value for the tooltip.
     * @returns {string}
     */
    tooltip: {
      type: Function,
      default: ({ value }) => value
    },
  },

  data () {
    return {
      tooltipVisible: false,
      tooltipContent: '',
      tooltipLeft: 0,
      tooltipTop: 0,
      tooltipTitle: '',
      tooltipMarker: {}
    }
  },

  computed: {
    options () {
      return {
        chart: {
          type: 'donut',
          width: this.size,
          height: this.size,
          toolbar: { show: false },
          events: {
            dataPointMouseEnter: (event, _, config) => {
              this.showTooltip(event, config)
              this.positionTooltip(event)
            },
            dataPointMouseLeave: () => {
              this.hideTooltip()
            },
            mounted: () => applyBlurStrokeFilters(this.$refs.apex, this.colors),
            updated: () => applyBlurStrokeFilters(this.$refs.apex, this.colors)
          }
        },
        colors: this.colors,
        labels: this.defaultLabels,
        dataLabels: { enabled: false },
        legend: { show: false },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: `${100 - ((90 / this.size) * 100)}%`
            }
          }
        },
        stroke: {
          show: true,
          width: 2,
          colors: this.colors
        },
        tooltip: { enabled: false }
      }
    },

    tooltipStyle () {
      return {
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: 1000
      }
    },

    defaultSeries () {
      const sorted = [ ...this.series ].sort((a, b) => b - a)

      if (sorted.length <= 6) return sorted

      const others = sorted.slice(5).reduce((acc, val) => acc + val, 0)

      return sorted.slice(0, 5).concat(others)
    },

    defaultLabels () {
      const sortedPairs = [ ...this.series ]
        .map((v, i) => ({ value: v, label: this.labels[i] }))
        .sort((a, b) => b.value - a.value)

      if (sortedPairs.length <= 6) {
        return sortedPairs.map(pair => pair.label)
      }

      return sortedPairs.slice(0, 5).map(pair => pair.label).concat('Outros')
    },

    colors () {
      return this.customColors?.length > 0 ? this.customColors : DEFAULT_COLORS
    },

    newSeries () {
      return this.customColors?.length > 0 ? this.series : this.defaultSeries
    },

    newLabels () {
      return this.customColors?.length > 0 ? this.labels : this.defaultLabels
    }
  },

  methods: {
    showTooltip (_, config) {
      const index = config.dataPointIndex
      const label = this.newLabels[index]
      const value = this.newSeries[index]

      const marker = this.customMarkers?.length > 0 ?
        this.customMarkers[index] :
        DEFAULT_MARKERS[index]
      this.tooltipContent = this.tooltip({ value, ...config })
      this.tooltipTitle = label
      this.tooltipVisible = true
      this.tooltipMarker = marker
    },

    hideTooltip () {
      this.tooltipVisible = false
    },

    positionTooltip (event) {
      if (event) {
        this.$nextTick(() => {
          const tooltipInstance = this.$refs.tooltip
          const popoverEl = tooltipInstance?.$refs?.popover?.$el

          const { width, height } = popoverEl.getBoundingClientRect()

          const tooltipOffsetX = width / 2
          const tooltipOffsetY = height + 10

          this.tooltipLeft = `${event.pageX - tooltipOffsetX}px`
          this.tooltipTop = `${event.pageY - tooltipOffsetY}px`
        })
      }
    },

    handleMouseMove (event) {
      if (this.tooltipVisible) {
        this.positionTooltip(event)
      }
    }
  },
}
</script>
